import {
  PAGE_SIZE,
  mapAvailableDetail,
  mapAvailableListItem,
} from "./availableFreight.mapper";

export { PAGE_SIZE };

export const HUB_TAB_AVAILABLE = "available";
export const HUB_TAB_IN_REVIEW = "in_review";
export const HUB_TAB_ACCEPTED = "accepted";
export const HUB_TAB_FINISHED = "finished";
export const HUB_TAB_LOST = "lost";

export const HUB_TABS = [
  {
    key: HUB_TAB_AVAILABLE,
    label: "Disponíveis",
    summaryKey: "available",
    emptyText: "Nenhum frete disponível para os filtros aplicados.",
  },
  {
    key: HUB_TAB_IN_REVIEW,
    label: "Em análise",
    summaryKey: "inReview",
    emptyText: "Nenhum frete em análise no momento.",
  },
  {
    key: HUB_TAB_ACCEPTED,
    label: "Aceitas",
    summaryKey: "accepted",
    emptyText: "Nenhum frete aceito no momento.",
  },
  {
    key: HUB_TAB_FINISHED,
    label: "Finalizadas",
    summaryKey: "finished",
    emptyText: "Nenhum frete finalizado no período.",
  },
  {
    key: HUB_TAB_LOST,
    label: "Perdidas",
    summaryKey: "lost",
    emptyText: "Nenhum frete perdido no momento.",
  },
];

export const EMPTY_HUB_SUMMARY = {
  available: 0,
  inReview: 0,
  accepted: 0,
  finished: 0,
  lost: 0,
};

/** Limite por request ao hub (paginação visual continua com PAGE_SIZE). */
export const HUB_FETCH_LIMIT = 100;

export function buildCarrierHubParams(
  filters = {},
  {
    tab = HUB_TAB_AVAILABLE,
    idCarrier,
    idUser,
    limit = HUB_FETCH_LIMIT,
    cursor,
  } = {},
) {
  const params = {
    tab: tab || HUB_TAB_AVAILABLE,
    limit,
    contractingType: "ALL",
  };

  if (idCarrier) params.idCarrier = idCarrier;
  if (idUser) params.idUser = idUser;
  if (cursor) params.cursor = cursor;

  const numberFilter = filters.number?.trim();
  if (numberFilter) {
    const normalized = numberFilter.replace(/^FT-/i, "").trim();
    if (/^\d+$/.test(normalized)) {
      params.numberSerial = normalized;
    } else {
      params.freightCode = numberFilter;
    }
  }

  // origem/destino: filtrados no front pela rota exibida (origin/destination).
  // A API nem sempre aplica o mesmo formato da lista (Cidade - UF vs Cidade/UF).
  if (filters.vehicleType) params.vehicleType = filters.vehicleType;
  if (filters.bodyType) params.bodyworkType = filters.bodyType;
  if (filters.collectionDate) params.collectionDate = filters.collectionDate;

  if (typeof filters.onlyMyShippers === "boolean") {
    params.onlyMyShippers = filters.onlyMyShippers;
  }

  return params;
}

export function mapCarrierHubSummary(summary) {
  const raw = summary && typeof summary === "object" ? summary : {};
  return {
    available: Number(raw.available) || 0,
    inReview: Number(raw.inReview ?? raw.in_review) || 0,
    accepted: Number(raw.accepted) || 0,
    finished: Number(raw.finished) || 0,
    lost: Number(raw.lost) || 0,
  };
}

function resolveIsMyClient(item) {
  if (typeof item?.isMyClient === "boolean") return item.isMyClient;
  if (typeof item?.isAffiliateShipper === "boolean") return item.isAffiliateShipper;
  return null;
}

/**
 * Item do hub → shape da UI de Lances (+ campos novos do contrato).
 */
export function mapCarrierHubListItem(item, options = {}) {
  const base = mapAvailableListItem(item, options);
  const contractingType = String(
    item?.contractingType || item?.contractType || base.contractType || "",
  ).toUpperCase();
  const isMyClient = resolveIsMyClient(item);

  return {
    ...base,
    contractingType: contractingType || base.contractType || "FIXED",
    contractingLabel:
      item?.contractingLabel ||
      base.contractingLabel ||
      (contractingType === "BID" ? "ABERTO A LANCE" : "PREÇO FIXO"),
    cargoType: item?.cargoType ?? item?.tipoCarga ?? null,
    paymentTermLabel:
      item?.paymentTermLabel ??
      item?.paymentTerm ??
      item?.prazoPagamento ??
      null,
    isMyClient: isMyClient === true,
    isAffiliateShipper: isMyClient === true,
    carrierApplicationStatus: item?.carrierApplicationStatus ?? null,
    carrierApplicationStatusLabel: item?.carrierApplicationStatusLabel ?? null,
    lostReason: item?.lostReason ?? null,
    publishedAt: item?.publishedAt ?? null,
    hubTab: item?.tab ?? null,
    modality: item?.modality ?? base.contractingLabel ?? base.modality,
  };
}

export function mapCarrierHubDetail(item, options = {}) {
  const base = mapAvailableDetail(item, options);
  const listExtras = mapCarrierHubListItem(item, options);
  return {
    ...base,
    ...listExtras,
    id: base.id || listExtras.id,
  };
}

export function parseCarrierHubResponse(response) {
  const root = response?.data?.data ?? response?.data ?? response ?? {};
  const summary = mapCarrierHubSummary(root.summary);
  const items = Array.isArray(root.items)
    ? root.items
    : Array.isArray(root.data)
      ? root.data
      : Array.isArray(root.freights)
        ? root.freights
        : [];

  const pagination = root.pagination ?? {};
  const totalRaw =
    pagination.total ??
    pagination.totalCount ??
    pagination.count ??
    root.count;
  const total =
    typeof totalRaw === "number" && Number.isFinite(totalRaw)
      ? totalRaw
      : items.length;

  return {
    summary,
    items,
    pagination: {
      hasMore: Boolean(pagination.hasMore ?? pagination.has_more),
      nextCursor: pagination.next_cursor ?? pagination.nextCursor ?? null,
      total,
    },
  };
}

export async function fetchCarrierHub(
  getCarrierHub,
  filters,
  { tab, idCarrier, idUser, limit = HUB_FETCH_LIMIT } = {},
) {
  const params = buildCarrierHubParams(filters, {
    tab,
    idCarrier,
    idUser,
    limit,
  });
  const response = await getCarrierHub(params);
  return parseCarrierHubResponse(response);
}

const MAX_HUB_FETCH_BATCHES = 50;

/**
 * Percorre a paginação por cursor do hub até esgotar (ou atingir o teto de lotes).
 */
export async function fetchAllCarrierHubPages(
  getCarrierHub,
  filters,
  { tab, idCarrier, idUser, limit = HUB_FETCH_LIMIT } = {},
) {
  const byId = new Map();
  let cursor;
  let summary = { ...EMPTY_HUB_SUMMARY };
  let totalFromApi = null;

  for (let batch = 0; batch < MAX_HUB_FETCH_BATCHES; batch += 1) {
    const params = buildCarrierHubParams(filters, {
      tab,
      idCarrier,
      idUser,
      limit,
      cursor,
    });
    const response = await getCarrierHub(params);
    const parsed = parseCarrierHubResponse(response);

    if (parsed.summary) summary = parsed.summary;
    if (typeof parsed.pagination?.total === "number") {
      totalFromApi = parsed.pagination.total;
    }

    (parsed.items || []).forEach((item) => {
      const id = item.freightId ?? item.id ?? item._id;
      if (id) byId.set(String(id), item);
    });

    const nextCursor = parsed.pagination?.nextCursor;
    if (!parsed.pagination?.hasMore || !nextCursor) break;
    if (nextCursor === cursor) break;
    cursor = nextCursor;
  }

  return {
    summary,
    items: Array.from(byId.values()),
    pagination: {
      hasMore: false,
      nextCursor: null,
      total: totalFromApi ?? byId.size,
    },
  };
}

/** Normaliza texto de rota para comparar filtro x item listado. */
export function normalizeRouteText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s*[-–—]\s*/g, "/")
    .replace(/\s*\/\s*/g, "/")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Compara filtro de origem/destino com a rota do card.
 * Aceita "Cidade - UF", "Cidade/UF" e busca parcial pelo nome da cidade.
 */
export function matchesRouteFilter(routeValue, filterValue) {
  const filter = normalizeRouteText(filterValue);
  if (!filter) return true;

  const route = normalizeRouteText(routeValue);
  if (!route) return false;

  if (route.includes(filter) || filter.includes(route)) return true;

  const [filterCity, filterUf = ""] = filter.split("/").map((part) => part.trim());
  const [routeCity, routeUf = ""] = route.split("/").map((part) => part.trim());

  if (!filterCity || !routeCity) return false;

  const cityMatches =
    routeCity.includes(filterCity) || filterCity.includes(routeCity);

  if (!cityMatches) return false;
  if (filterUf && routeUf) return filterUf === routeUf;
  return true;
}

export function filterFreightsByRoute(freights, filters = {}) {
  const list = Array.isArray(freights) ? freights : [];
  const origin = filters.origin || "";
  const destination = filters.destination || "";

  if (!origin.trim() && !destination.trim()) return list;

  return list.filter(
    (freight) =>
      matchesRouteFilter(freight?.origin, origin) &&
      matchesRouteFilter(freight?.destination, destination),
  );
}

export function getHubTabMeta(tabKey) {
  return (
    HUB_TABS.find((tab) => tab.key === tabKey) ||
    HUB_TABS.find((tab) => tab.key === HUB_TAB_AVAILABLE)
  );
}

export function getSummaryCountForTab(summary, tabKey) {
  const meta = getHubTabMeta(tabKey);
  const key = meta?.summaryKey;
  if (!key || !summary) return 0;
  return Number(summary[key]) || 0;
}
